import React from 'react';
import { Query, type QueryComponentOptions } from '@apollo/react-components';
import { useQuery } from '@apollo/react-hooks';
import gqlTagSwag from 'graphql-tag';
import type { BurnupChartData, SprintReportsEstimationStatistic } from '../../../common/types';

export const BURNUP_DATA_QUERY = gqlTagSwag`
    query BurnupDataQuery(
        $boardId: ID!
        $sprintId: ID
        $estimation: SprintReportsEstimationStatistic
    ) {
        boardScope(boardId: $boardId) {
            reports {
                burnupChart {
                    chart(sprintId: $sprintId, estimation: $estimation) {
                        startTime
                        endTime
                        sprintStartEvent {
                            scopeEstimate
                            timestamp
                            issueList {
                                issueKey
                                issueSummary
                                estimate
                            }
                        }
                        scopeChangeEvents {
                            timestamp
                            issueKey
                            issueSummary
                            eventType
                            scope
                            completion
                            estimate
                            prevScope
                            prevCompletion
                            prevEstimate
                        }
                        sprintEndEvent {
                            scope
                            issueList {
                                issueKey
                                issueSummary
                                estimate
                            }
                            timestamp
                            completion
                        }
                        timeZone
                    }
                }
            }
        }
    }
`;

export type Variables = {
	boardId: string | number;
	sprintId: null | string | number;
	estimation: null | SprintReportsEstimationStatistic;
};

type Response = {
	boardScope: {
		reports: {
			burnupChart: {
				chart: BurnupChartData;
			};
		};
	};
};

export type Props = Omit<QueryComponentOptions<Response, Variables>, 'query'>;

/**
 * Apollo Query for the burnup chart data.
 */
const BurnupDataQuery = (props: Props) => (
	<Query<Response, Variables> {...props} query={BURNUP_DATA_QUERY} />
);

export const useBurnupDataQuery = (variables: Variables) =>
	useQuery<Response, Variables>(BURNUP_DATA_QUERY, {
		variables,
		notifyOnNetworkStatusChange: true,
	});

export default BurnupDataQuery;
